import {
  InlineShippingOptions,
  Product as GqlProduct,
  ProductInput,
  ProductType,
  ProductVariant,
  SellerSettings,
  WeightUnit,
} from '@shopify-multiseller/graphql/partner/types';
import { v4 as uuid } from 'uuid';
import { IProductInitialValuesProps } from './types';
import {
  removeTypenames,
  resolveImages,
  resolveInitialProductType,
  resolveInitialStandardShipping,
  resolveInitialTrackQuantity,
  resolveOptions,
  resolveShippingType,
  resolveVariantOptions,
} from './utils';

/**
 * Pure mapping helpers for the product editor.
 *
 * The editor works on a local Formik copy (`IProductInitialValuesProps`) that is
 * derived from the loaded product, and produces a `ProductInput` on save. Keeping
 * these transforms out of the component makes them testable and removes the
 * near-duplicate pending/current branches that previously lived inline in effects.
 */

type ProductFormVariant = IProductInitialValuesProps['variants'][number];

/**
 * Map a single stored variant to its editable form shape.
 *
 * `honorIsNew` mirrors the two original code paths: the `pending` source keeps a
 * variant's `isNew` flag (new variants carry `initialQuantity`, existing ones
 * carry `quantityAdjustment`), whereas the `current` source always treats every
 * variant as existing.
 */
const mapVariant = (variant: ProductVariant, honorIsNew: boolean): ProductFormVariant => {
  const base = {
    id: variant.id,
    price: variant.price,
    sku: variant.sku || '',
    showSku: !!variant.sku,
    barcode: variant.barcode || '',
    imageId: variant.image?.id || undefined,
    variantOptions: variant.variantOptions ? resolveVariantOptions(variant.variantOptions) : undefined,
    compareAtPrice: variant.compareAtPrice || undefined,
    trackQuantity: variant.trackQuantity || false,
    weight: variant.weight || undefined,
    weightUnit: variant.weightUnit || WeightUnit.Kilograms,
  };

  if (honorIsNew && variant.isNew) {
    return { ...base, isNew: true, initialQuantity: variant.initialQuantity || 0 };
  }
  return { ...base, isNew: false, quantityAdjustment: variant.quantityAdjustment || 0 };
};

/**
 * Derive the editor's initial Formik values from a loaded product.
 *
 * Prefers the `pending` snapshot (a product being reviewed) and falls back to the
 * live `current` snapshot. Returns the raw variants (for the variant editor) and
 * the initial `trackQuantity` (or `undefined` when there are no variants, meaning
 * "leave the current value untouched").
 */
export const productToFormValues = (
  product: GqlProduct,
  settings: SellerSettings,
): {
  input: IProductInitialValuesProps;
  variants: ProductVariant[] | undefined;
  trackQuantity: boolean | undefined;
} => {
  const usePending = !!product.pending;
  const source = usePending ? product.pending : product.current;
  const sourceVariants = source?.variants;

  const input: IProductInitialValuesProps = {
    id: product.id,
    productType: source?.productType || undefined,
    allowDeliveryProfile: settings.productShipping.allowProfiles,
    internationalShipping:
      settings.productShipping.inlineShippingOptions === InlineShippingOptions.InternationalMandatory,
    shippingType: source ? resolveShippingType(source) : undefined,
    title: source?.title || '',
    options: source?.options ? resolveOptions(source.options) : undefined,
    description: source?.description || '',
    images: source?.images ? resolveImages(source.images) : undefined,
    standardShipping: source?.standardShipping || undefined,
    deliveryProfileId: source?.deliveryProfile?.id || undefined,
    variants: sourceVariants?.map((variant) => mapVariant(variant, usePending)) || [],
  };

  const trackQuantity =
    sourceVariants && sourceVariants.length > 0 ? sourceVariants[0].trackQuantity || false : undefined;

  return { input, variants: sourceVariants ?? undefined, trackQuantity };
};

/**
 * Build the empty Formik values for the "add product" flow, seeding shipping /
 * quantity defaults from the seller's settings.
 */
export const buildEmptyProductInput = (
  settings: SellerSettings,
  shopCountryCode?: string,
  sellerCountryCode?: string,
): { input: IProductInitialValuesProps; trackQuantity: boolean } => {
  const productTypeOptions = settings.productType?.productType;
  const trackQuantity = resolveInitialTrackQuantity(productTypeOptions);
  const productType = resolveInitialProductType(productTypeOptions);
  const shippingType = productType === ProductType.Physical ? 'standard' : undefined;
  const standardShipping =
    shippingType === 'standard'
      ? resolveInitialStandardShipping(settings.productShipping, shopCountryCode, sellerCountryCode)
      : undefined;

  const input: IProductInitialValuesProps = {
    id: uuid(),
    title: '',
    description: '',
    productType,
    allowDeliveryProfile: settings.productShipping.allowProfiles,
    internationalShipping:
      settings.productShipping.inlineShippingOptions === InlineShippingOptions.InternationalMandatory,
    shippingType,
    standardShipping,
    images: [],
    variants: [
      {
        id: uuid(),
        price: 0,
        sku: '',
        showSku: false,
        barcode: undefined,
        isNew: true,
        quantityAdjustment: 0,
        initialQuantity: 0,
        compareAtPrice: undefined,
        trackQuantity,
        weight: 0,
        weightUnit: WeightUnit.Kilograms,
      },
    ],
  };

  return { input, trackQuantity };
};

/**
 * Convert the editor's Formik values into the GraphQL `ProductInput` used by both
 * the create and update mutations. `__typename` fields are stripped because Apollo
 * cache metadata is not part of the input type.
 */
export const formValuesToProductInput = (
  values: IProductInitialValuesProps,
  id: string,
): ProductInput =>
  removeTypenames({
    id,
    title: values.title,
    description: values.description,
    options: values.options,
    images: values.images,
    variants: values.variants.map((variant) => ({
      id: variant.id,
      price: variant.price,
      sku: variant.sku,
      barcode: variant.barcode || undefined,
      compareAtPrice: variant.compareAtPrice,
      trackQuantity: variant.trackQuantity,
      quantityAdjustment: variant.quantityAdjustment,
      initialQuantity: variant.initialQuantity,
      isNew: variant.isNew,
      weight: variant.weight,
      weightUnit: variant.weightUnit,
      taxable: variant.taxable,
      variantOptions: variant.variantOptions,
      imageId: variant.imageId,
    })),
    standardShipping: values.standardShipping,
    deliveryProfileId: values.deliveryProfileId,
    productType: values.productType!,
  });
